/** 通知中心展示件：数据与写操作由应用注入，不新增轮询或跨包运行期依赖。 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Bell, X } from './icons'
import { useOverlay } from './use-overlay'

type Notice = { id: number; title: string; content: string; link: string; readAt: string | null; createdAt: string }
export type NotificationBellProps = {
  load: (cursor?: string) => Promise<{ items: Notice[]; unreadCount: number; hasMore: boolean; nextCursor: string | null }>
  markRead: (input: { ids: number[] } | { all: true }) => Promise<unknown>
  refreshKey?: string
  align?: 'topbar' | 'sidebar'
}

export function NotificationBell({ load, markRead, refreshKey, align = 'topbar' }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<Notice[]>([])
  const [unread, setUnread] = useState(0)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [position, setPosition] = useState<{ left: number; top?: number; bottom?: number }>({ left: 12, top: 64 })
  const trigger = useRef<HTMLButtonElement>(null)
  const sequence = useRef(0)
  const id = useId()
  const close = useCallback(() => setOpen(false), [])
  const panel = useOverlay<HTMLDivElement>(open, close)
  const refresh = useCallback(async (next?: string) => {
    const request = ++sequence.current
    setLoading(true)
    setError('')
    try {
      const data = await load(next)
      if (request !== sequence.current) return
      setItems((current) => next ? [...current, ...data.items.filter((item) => !current.some((row) => row.id === item.id))] : data.items)
      setUnread(data.unreadCount)
      setCursor(data.hasMore ? data.nextCursor : null)
    } catch (reason) {
      if (request === sequence.current) setError(reason instanceof Error ? reason.message : '通知加载失败，请重试')
    } finally { if (request === sequence.current) setLoading(false) }
  }, [load])

  useEffect(() => {
    void refresh()
    const focus = () => { void refresh() }
    window.addEventListener('focus', focus)
    return () => { sequence.current++; window.removeEventListener('focus', focus) }
  }, [refresh, refreshKey])
  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', close)
    return () => window.removeEventListener('resize', close)
  }, [open, close])

  const read = async (input: { ids: number[] } | { all: true }) => {
    setSaving(true)
    setError('')
    try { await markRead(input); await refresh() }
    catch (reason) { setError(reason instanceof Error ? reason.message : '标记已读失败，请重试') }
    finally { setSaving(false) }
  }
  const show = () => {
    const rect = trigger.current!.getBoundingClientRect()
    const width = Math.min(380, window.innerWidth - 24)
    setPosition({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
      ...(align === 'sidebar' ? { bottom: Math.max(12, window.innerHeight - rect.top + 8) } : { top: rect.bottom + 8 }) })
    setOpen(true)
    void refresh()
  }
  const actionClass = 'inline-flex min-h-11 items-center justify-center rounded-[var(--radius-sm)] px-3 text-xs text-[var(--color-muted)] hover:bg-[var(--color-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-focus)] disabled:opacity-50'

  return <>
    <button ref={trigger} type="button" aria-label={error ? '通知中心，加载失败' : `通知中心，${unread} 条未读`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={show} className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-muted)] hover:bg-[var(--color-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]">
      <Bell size={19} />
      {unread > 0 ? <span aria-hidden="true" className="absolute right-0.5 top-0.5 min-w-4 rounded-[var(--radius-badge)] bg-[var(--color-primary)] px-1 text-center text-[10px] font-semibold leading-4 text-[var(--color-on-primary)]">{unread > 99 ? '99+' : unread}</span> : null}
      {error ? <span aria-hidden="true" className="absolute right-1 top-1 text-xs text-[var(--color-danger)]">!</span> : null}
    </button>
    {open ? createPortal(<div className="fixed inset-0 z-50">
      <div className="absolute inset-0" aria-hidden="true" onClick={close} />
      <div ref={panel} id={id} role="dialog" aria-modal="true" aria-label="通知中心" tabIndex={-1} style={position}
        className="fixed flex max-h-[min(600px,80dvh)] w-[min(380px,calc(100vw-24px))] flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel)] text-[var(--color-ink)] shadow-[var(--shadow-lg)]">
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-line)] px-4 py-1">
          <h2 className="text-sm font-semibold">通知中心 <span className="ml-1 text-xs font-normal text-[var(--color-muted)]">{unread} 条未读</span></h2>
          <button type="button" className={actionClass + ' w-11 px-0'} aria-label="关闭通知中心" onClick={close}><X size={16} /></button>
        </div>
        <div className="flex shrink-0 justify-between px-1">
          <button type="button" className={actionClass} disabled={loading || saving} onClick={() => { void refresh() }}>刷新通知</button>
          <button type="button" className={actionClass} disabled={!unread || saving || loading} onClick={() => { void read({ all: true }) }}>全部标为已读</button>
        </div>
        {error ? <p role="alert" className="px-4 pb-3 text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="min-h-0 overflow-y-auto" aria-busy={loading}>
          {items.map((item) => {
            const safeLink = item.link.startsWith('/') && !item.link.startsWith('//') && !item.link.includes('\\')
            return <article key={item.id} className={`border-t border-[var(--color-line)] px-4 py-3 ${item.readAt ? '' : 'bg-[var(--color-soft)]'}`}>
              {safeLink ? <a href={item.link} className="flex min-h-11 items-center gap-2 text-sm font-medium hover:underline">
                {!item.readAt ? <span aria-label="未读" className="h-1.5 w-1.5 shrink-0 rounded-[var(--radius-badge)] bg-[var(--color-primary)]" /> : null}
                <span className="min-w-0 break-words">{item.title}</span>
              </a> : <h3 className="break-words text-sm font-medium">{item.title}</h3>}
              <p className="mt-1 line-clamp-3 break-words text-sm leading-relaxed text-[var(--color-muted)]">{item.content}</p>
              <div className="mt-1 flex items-center justify-between gap-2">
                <time className="text-xs text-[var(--color-muted)]">{item.createdAt}</time>
                {!item.readAt ? <button type="button" className={actionClass} disabled={saving || loading} onClick={() => { void read({ ids: [item.id] }) }}>标为已读</button> : <span className="py-3 text-xs text-[var(--color-muted)]">已读</span>}
              </div>
            </article>
          })}
          {loading ? <p role="status" className="px-4 py-3 text-sm text-[var(--color-muted)]">正在加载通知…</p> : null}
          {!loading && !error && items.length === 0 ? <div className="px-4 py-7 text-center"><p className="text-sm">暂无通知</p><p className="mt-1 text-xs text-[var(--color-muted)]">系统升级后，说明会出现在这里。</p><button type="button" className={actionClass + ' mt-2'} onClick={close}>继续工作</button></div> : null}
          {cursor ? <button type="button" className={actionClass + ' w-full'} disabled={loading || saving} onClick={() => { void refresh(cursor) }}>加载更多</button> : null}
        </div>
      </div>
    </div>, document.body) : null}
  </>
}
